<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-lg font-medium">Mes Adresses</h3>
      <UButton
        icon="i-lucide-plus"
        size="sm"
        color="black"
        @click="isAddressModalOpen = true"
      >
        Ajouter une adresse
      </UButton>
    </div>

    <div v-if="pending" class="space-y-4">
      <USkeleton v-for="i in 3" :key="i" class="h-24 w-full" />
    </div>

    <div
      v-else-if="addresses.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      <div
        v-for="item in addresses"
        :key="item.id"
        class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 relative group"
      >
        <div class="pr-8">
          <p class="font-medium text-gray-900 dark:text-white">
            {{ item.address.address_1 }}
          </p>
          <p v-if="item.address.address_2" class="text-sm text-gray-500">
            {{ item.address.address_2 }}
          </p>
          <p class="text-sm text-gray-500 mt-1">
            {{ item.address.zip_code }} {{ item.address.city }}
          </p>
        </div>
        <UButton
          icon="i-lucide-trash-2"
          color="red"
          variant="ghost"
          size="xs"
          class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          :loading="deleting === item.id"
          @click="remove(item.id)"
        />
      </div>
    </div>

    <div v-else class="text-center py-10 text-gray-500">
      Aucune adresse enregistrée.
    </div>

    <UModal v-model:open="isAddressModalOpen">
      <template #content>
        <UCard>
          <template #header>
            <div class="flex items-center justify-between">
              <h3
                class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
              >
                Nouvelle adresse
              </h3>
              <UButton
                color="gray"
                variant="ghost"
                icon="i-heroicons-x-mark-20-solid"
                class="-my-1"
                @click="isAddressModalOpen = false"
              />
            </div>
          </template>

          <UForm
            :schema="schema"
            :state="form"
            class="space-y-4"
            autocomplete="off"
            @submit="addAddress"
          >
            <UFormField label="Adresse" name="address_1">
              <UInput
                v-model="form.address_1"
                placeholder="123 Rue de Exemple"
                color="main"
                size="lg"
                class="w-full"
                autocomplete="off"
              />
            </UFormField>

            <UFormField label="Complément" name="address_2">
              <UInput
                v-model="form.address_2"
                placeholder="Appt, Batiment..."
                color="main"
                size="lg"
                class="w-full"
                autocomplete="off"
              />
            </UFormField>

            <div class="grid grid-cols-2 gap-4">
              <UFormField label="Ville" name="city">
                <UInputMenu
                  v-model="form.city"
                  :items="availableCities"
                  value-key="id"
                  placeholder="Sélectionner une ville"
                  color="main"
                  size="lg"
                  class="w-full"
                  :loading="loadingCities"
                  searchable
                />
              </UFormField>

              <UFormField label="Code Postal" name="zip_code">
                <UInput
                  v-model="form.zip_code"
                  color="main"
                  size="lg"
                  class="w-full"
                  placeholder="Ex: 75001"
                  autocomplete="off"
                />
              </UFormField>
            </div>

            <div class="flex justify-end pt-4">
              <UButton type="submit" :loading="saving" color="main" size="lg">
                Ajouter
              </UButton>
            </div>
          </UForm>
        </UCard>
      </template>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import { z } from "zod";
import type { FormSubmitEvent } from "#ui/types";
import type { InputMenuItem } from "@nuxt/ui";

const toast = useToast();

const isAddressModalOpen = ref(false);
watch(isAddressModalOpen, (val) => console.log("Address Modal Open:", val));

const saving = ref(false);
const deleting = ref<number | null>(null);

const schema = z.object({
  address_1: z.string().min(5, "Adresse trop courte"),
  address_2: z.string().optional(),
  zip_code: z.string().regex(/^\d{5}$/, "Code postal invalide (5 chiffres)"),
  city: z.string().min(2, "Ville requise"),
});

type Schema = z.output<typeof schema>;

const form = reactive({
  address_1: "",
  address_2: "",
  zip_code: "",
  city: "",
  coordinates: "",
});

const addresses = ref<InputMenuItem[]>([]);
const pending = ref(true);

const availableCities = ref<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
const loadingCities = ref(false);

const fetchAddresses = async () => {
  pending.value = true;
  try {
    const { $api } = useNuxtApp();
    addresses.value = await $api.get<any[]>("/user-addresses", "include"); // eslint-disable-line @typescript-eslint/no-explicit-any
  } catch {
    // console.error(e);
  } finally {
    pending.value = false;
  }
};

const fetchCities = async () => {
  loadingCities.value = true;
  try {
    const { $api } = useNuxtApp();
    const data = await $api.get<any[]>("/delivery/all-cities"); // eslint-disable-line @typescript-eslint/no-explicit-any
    // Map to { id, label, ... } structure as requested
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tmp = data.map((c: any) => ({
      id: c.uuid,
      label: c.city,
      lat: c.lat,
      lng: c.lng,
    }));
    availableCities.value = tmp;
  } catch (e) {
    console.error("Failed to fetch cities", e);
  } finally {
    loadingCities.value = false;
  }
};

onMounted(() => {
  fetchAddresses();
  fetchCities();
});

const addAddress = async (event: FormSubmitEvent<Schema>) => {
  saving.value = true;
  try {
    const { $api } = useNuxtApp();

    // form.city is now the ID. Find the full object.
    const selectedCity = availableCities.value.find(
      (c) => c.id === event.data.city,
    );

    // Prepare payload: Swap ID back to Label (City Name) for the backend
    const payload = {
      ...event.data,
      city: selectedCity ? selectedCity.label : event.data.city,
    };

    if (selectedCity && selectedCity.lat && selectedCity.lng) {
      payload.coordinates = `${selectedCity.lat},${selectedCity.lng}`;
    }

    await $api.post("/user-addresses", payload, "include");

    toast.add({ title: "Adresse ajoutée", color: "green" });
    isAddressModalOpen.value = false;
    await fetchAddresses();
    // Reset form
    form.address_1 = "";
    form.address_2 = "";
    form.zip_code = "";
    form.city = "";
  } catch {
    toast.add({ title: "Erreur lors de l'ajout", color: "red" });
  } finally {
    saving.value = false;
  }
};

const remove = async (id: number) => {
  if (!confirm("Voulez-vous supprimer cette adresse ?")) return;
  deleting.value = id;
  try {
    const { $api } = useNuxtApp();
    await $api.del(`/user-addresses/${id}`, "include");
    toast.add({ title: "Adresse supprimée", color: "green" });
    await fetchAddresses();
  } catch {
    toast.add({ title: "Erreur", color: "red" });
  } finally {
    deleting.value = null;
  }
};
</script>
